/**
 * SupportEmailModal Component
 * 
 * Modal dialog for sending support emails to administrators.
 * Provides a clear form with information about what will happen.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import './SupportEmailModal.css';

interface SupportEmailModalProps {
  onClose: () => void;
}

const SupportEmailModal: React.FC<SupportEmailModalProps> = ({ onClose }) => {
  const { user, token } = useAuth();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const subject = 'Support-Anfrage - Helfermanagement Römerman 2026';

  // Don't fetch sender email - backend will get it from database server-side
  // This avoids permission issues and protects user data

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending && !success) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, sending, success]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError('Bitte gib eine Nachricht ein.');
      return;
    }

    if (!token) {
      setError('Du bist nicht angemeldet. Bitte melde dich erneut an.');
      return;
    }

    setError(null);
    setSending(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/support/email`,
        {
          message: message.trim(),
          senderName: user?.username || undefined
          // senderEmail not sent - backend will fetch it from database
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setMessage('');
      } else {
        setError(response.data.message || 'Fehler beim Senden der E-Mail');
      }
    } catch (err: any) {
      console.error('Error sending support email:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Fehler beim Senden der E-Mail';
      setError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending && !success) {
      onClose();
    }
  };

  const handleSendAnother = () => {
    setSuccess(false);
    setMessage('');
    setError(null);
  };

  return (
    <div 
      className="support-email-overlay" 
      onClick={handleClose}
    >
      <div 
        className="support-email-container" 
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          className="support-email-close" 
          onClick={handleClose}
          disabled={sending}
          title="Schließen"
        >
          ×
        </button>

        <div className="support-email-header">
          <h2 className="support-email-title">Support-Anfrage senden</h2>
        </div>

        {success ? (
          <div className="support-email-content">
            <div className="support-email-success">
              <div className="support-email-success-icon">✓</div>
              <h3>E-Mail erfolgreich gesendet!</h3>
              <p>
                Deine Support-Anfrage wurde an alle Administratoren gesendet. 
                Du erhältst in Kürze eine Antwort.
              </p>
              <div className="support-email-actions">
                <button
                  type="button"
                  className="support-email-button support-email-button-primary"
                  onClick={handleSendAnother}
                >
                  Weitere Nachricht senden
                </button>
                <button
                  type="button"
                  className="support-email-button support-email-button-secondary"
                  onClick={onClose}
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="support-email-content">
            <form onSubmit={handleSubmit} className="support-email-form">
              <div className="support-email-header-fields">
                <div className="support-email-field">
                  <label htmlFor="support-from">Von:</label>
                  <input
                    type="text"
                    id="support-from"
                    value={user?.username || 'Unbekannter Benutzer'}
                    disabled
                    className="support-email-input support-email-input-disabled"
                  />
                </div>
                <div className="support-email-field">
                  <label htmlFor="support-to">An:</label>
                  <input
                    type="text"
                    id="support-to"
                    value="Alle Administratoren"
                    disabled
                    className="support-email-input support-email-input-disabled"
                  />
                </div>
                <div className="support-email-field">
                  <label htmlFor="support-subject">Betreff:</label>
                  <input
                    type="text"
                    id="support-subject"
                    value={subject}
                    disabled
                    className="support-email-input support-email-input-disabled"
                  />
                </div>
              </div>

              <div className="support-email-form-group">
                <label htmlFor="support-message">
                  Nachricht <span className="required">*</span>
                </label>
                <textarea
                  id="support-message"
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    setError(null);
                  }}
                  placeholder="Deine Nachricht..."
                  rows={12}
                  disabled={sending}
                  required
                  autoFocus
                />
                <div className="support-email-char-count">
                  {message.length} Zeichen
                </div>
              </div>

              {error && (
                <div className="support-email-error">
                  {error}
                </div>
              )}

              <div className="support-email-actions">
                <button
                  type="button"
                  className="support-email-button support-email-button-secondary"
                  onClick={handleClose}
                  disabled={sending}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="support-email-button support-email-button-primary"
                  disabled={sending || !message.trim()}
                >
                  {sending ? 'Wird gesendet...' : 'E-Mail senden'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportEmailModal;

